import {controller, target, attr} from '@github/catalyst'
import {eventToHotkeyString} from '@github-ui/hotkey'
import {fetchSafeDocumentFragment} from '@github-ui/fetch-utils'
import type {SidebarPinnedTopicsElement} from '@github-ui/sidebar-pinned-topics-element'

@controller
export class TopicFeedsContainerElement extends HTMLElement {
  private setTimeoutId: number

  @attr query: string
  @attr src: string

  @target content: HTMLElement

  searchInput: HTMLInputElement | null
  clearButton: HTMLElement

  connectedCallback() {
    this.updateContent()

    this.addEventListener('social:success', this.refreshPinnedTopics)

    const observer = new MutationObserver(() => {
      this.searchInput = this.querySelector('.js-topic-feeds-container-search')
      const button = this.querySelector('#topic-feeds-container-clear-button')
      if (button) {
        this.clearButton = button as HTMLElement
        this.clearButton.addEventListener('click', this.handleClearSearch.bind(this))
        this.clearButton.toggleAttribute('hidden', !this.query)
        this.clearButton.style.borderRadius = '3px'
        observer.disconnect()
      }
    })

    observer.observe(this, {childList: true, subtree: true})
  }

  attributeChangedCallback(name: string) {
    switch (name) {
      case 'data-query':
        this.toggleClearButton()
        break
    }
  }

  async refreshPinnedTopics() {
    const pinnedTopics: SidebarPinnedTopicsElement | null = document.querySelector('sidebar-pinned-topics')
    if (!pinnedTopics) {
      return
    }

    await pinnedTopics.reload()
  }

  handleSearchInput(evt: KeyboardEvent) {
    if (eventToHotkeyString(evt) === 'Escape') {
      evt.stopImmediatePropagation()
    }

    this.debounceQuery((evt.target as HTMLInputElement).value)
  }

  handleClearSearch() {
    if (!this.searchInput) return
    this.searchInput.value = ''
    this.debounceQuery('', 0)
  }

  private async updateContent() {
    const src = this.query ? `${this.src}?q=${this.query}` : this.src
    const fragment = await fetchSafeDocumentFragment(document, src)
    this.content.replaceChildren(fragment)
  }

  private debounceQuery(query: string, timeoutDuration = 250) {
    this.query = query
    clearTimeout(this.setTimeoutId)
    this.setTimeoutId = setTimeout(() => this.updateContent(), timeoutDuration) as unknown as number
  }

  private toggleClearButton() {
    if (this.clearButton) {
      this.clearButton.toggleAttribute('hidden', !this.query)
    }
  }
}
